/** Excel serial date → UTC calendar Date (1900 date system, no local TZ shift). */
export function excelSerialToDate(serial) {
  const n = Math.round(Number(serial));
  if (!Number.isFinite(n) || n < 20000 || n > 120000) return null;
  const ms = Date.UTC(1899, 11, 30) + n * 86400000;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function ymdFromUtcDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/** Calendar date from local Date parts (SheetJS cellDates in browser locale). */
export function ymdFromLocalDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** @deprecated use ymdFromUtcDate or ymdFromLocalDate */
export function toYmd(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const utcMidnight =
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  return utcMidnight ? ymdFromUtcDate(d) : ymdFromLocalDate(d);
}

/**
 * Parse dd/mm/yyyy (Indian SQL export) directly to YYYY-MM-DD — no timezone.
 */
export function dmyStringToYmd(s) {
  const m = String(s ?? "")
    .trim()
    .match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s|T|$)/);
  if (!m) return "";
  let y = Number(m[3]);
  if (y < 100) y += 2000;
  const day = Number(m[1]);
  const month = Number(m[2]);
  if (!y || day < 1 || day > 31 || month < 1 || month > 12) return "";
  return `${y}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function excelSerialToYmd(serial) {
  const d = excelSerialToDate(serial);
  return d ? ymdFromUtcDate(d) : "";
}

/** Read TranDate from row object (canonical or legacy keys). */
export function rowTranDateValue(row) {
  if (!row || typeof row !== "object") return "";
  return (
    row.tranDate ??
    row.TranDate ??
    row.trandate ??
    row["Trans Date"] ??
    row.transDate ??
    row.TransDate ??
    ""
  );
}

/** Parse TranDate from SQL export / Firestore (dd/mm/yyyy, iso, Excel serial, Date). */
export function parseTranDateLoose(v) {
  if (v == null || v === "") return null;

  if (typeof v === "number" && Number.isFinite(v)) {
    if (v > 20000 && v < 120000) return excelSerialToDate(v);
    if (v > 1e11) return new Date(v);
  }

  const s = String(v).trim();
  if (!s) return null;

  const dmyYmd = dmyStringToYmd(s);
  if (dmyYmd) {
    const [y, m, day] = dmyYmd.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, day));
  }

  const isoHead = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoHead) {
    return new Date(
      Date.UTC(Number(isoHead[1]), Number(isoHead[2]) - 1, Number(isoHead[3]))
    );
  }

  const serial = Number(s);
  if (Number.isFinite(serial) && serial > 20000 && serial < 120000) {
    return excelSerialToDate(serial);
  }

  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;

  const isoTail = s.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoTail) {
    return new Date(
      Date.UTC(Number(isoTail[1]), Number(isoTail[2]) - 1, Number(isoTail[3]))
    );
  }

  const iso = Date.parse(s);
  if (!Number.isNaN(iso)) return new Date(iso);

  return null;
}

/** Normalize TranDate cell to YYYY-MM-DD for storage and comparisons. */
export function normalizeTranDateString(v) {
  if (v == null || v === "") return "";

  const dmyYmd = dmyStringToYmd(v);
  if (dmyYmd) return dmyYmd;

  if (typeof v === "number" && Number.isFinite(v)) {
    const fromSerial = excelSerialToYmd(v);
    if (fromSerial) return fromSerial;
  }

  const s = String(v).trim();
  if (s) {
    const fromStrDmy = dmyStringToYmd(s);
    if (fromStrDmy) return fromStrDmy;

    const isoHead = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoHead) return `${isoHead[1]}-${isoHead[2]}-${isoHead[3]}`;

    const serial = Number(s);
    if (Number.isFinite(serial) && serial > 20000 && serial < 120000) {
      const fromSerial = excelSerialToYmd(serial);
      if (fromSerial) return fromSerial;
    }
  }

  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const utcMidnight =
      v.getUTCHours() === 0 &&
      v.getUTCMinutes() === 0 &&
      v.getUTCSeconds() === 0 &&
      v.getUTCMilliseconds() === 0;
    return utcMidnight ? ymdFromUtcDate(v) : ymdFromLocalDate(v);
  }

  const d = parseTranDateLoose(v);
  if (!d) return s;
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
    return ymdFromUtcDate(d);
  }
  return ymdFromLocalDate(d);
}

export function rowTranDateYmd(row) {
  return normalizeTranDateString(rowTranDateValue(row));
}

export function rowMatchesDateRange(row, fromDate, toDate) {
  if (!fromDate && !toDate) return true;
  const ymd = rowTranDateYmd(row);
  if (!ymd) return false;
  if (fromDate && ymd < fromDate) return false;
  if (toDate && ymd > toDate) return false;
  return true;
}

export function countRowsOutsideDateRange(rows, fromDate, toDate) {
  if (!fromDate && !toDate) return 0;
  let n = 0;
  for (const row of rows || []) {
    if (!rowMatchesDateRange(row, fromDate, toDate)) n += 1;
  }
  return n;
}

export function weekRangeOverlapsFilter(weekFrom, weekTo, filterFrom, filterTo) {
  if (filterFrom && weekTo < filterFrom) return false;
  if (filterTo && weekFrom > filterTo) return false;
  return true;
}

/** Months (year + monthIndex) overlapping a YMD date filter. */
export function monthsOverlappingRange(fromDate, toDate) {
  if (!fromDate && !toDate) return [];
  const start = fromDate ? new Date(`${fromDate}T12:00:00`) : new Date(`${toDate}T12:00:00`);
  const end = toDate ? new Date(`${toDate}T12:00:00`) : fromDate ? new Date() : start;
  const out = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  while (y < endY || (y === endY && m <= endM)) {
    out.push({ year: y, monthIndex: m });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return out;
}
